import Container from '../components/layout/Container';
import bracketImg from '../assets/screenshots/bracket-view.jpg';
import homeImg from '../assets/screenshots/home-screen.jpg';
import roomImg from '../assets/screenshots/room-creation.jpg';
import liveRoomImg from '../assets/screenshots/live-room.jpg';
import webViewerImg from '../assets/screenshots/web-viewer.jpg';
import scorerImg from '../assets/screenshots/scorer-dialog.jpg';
import appIcon from '../assets/app-icon.png';
import googlePlayBadge from '../assets/google-play-badge.png';
import './AppLandingPage.css';

const FREE_FEATURES = [
  { icon: '🎱', title: 'Tournament Brackets', desc: 'Single elimination, 9-ball, and more — tap the winner, done.' },
  { icon: '🔫', title: 'Killer Pool', desc: 'Full game management with lives, final shot rule, and death animations.' },
  { icon: '🏆', title: 'Round Robin & Leagues', desc: 'Automatic standings, scheduling, and results tracking.' },
  { icon: '⛳', title: 'Golf Pool & Speed Pool', desc: 'Timed and scored formats with built-in shot clock.' },
  { icon: '📊', title: 'Score Tracker', desc: 'Generic score tracking for any format you invent.' },
  { icon: '📄', title: 'PDF Export', desc: 'Share brackets and results as printable PDFs.' },
  { icon: '👥', title: 'Watch Live Rooms', desc: 'Join any shared room and follow the bracket in real time — no subscription needed.' },
  { icon: '🔗', title: 'Web Viewer', desc: 'Anyone can watch at pool.porkenpence.com/room/CODE — no app required.' },
];

const PRO_FEATURES = [
  { icon: '📡', title: 'Go Live', desc: 'Share your tournament as a live room. Viewers follow along on any device.' },
  { icon: '✏️', title: 'Score Submission', desc: 'Scorers at the table submit scores directly. Organiser approves or rejects.' },
  { icon: '🔒', title: 'Scorer Codes', desc: 'Restrict who can submit scores with a private 6-character code.' },
  { icon: '🎊', title: 'Champion Celebration', desc: 'Confetti and trophy overlay when the final match is decided.' },
];

export default function AppLandingPage() {
  return (
    <div className="app-landing">
      {/* Hero */}
      <section className="app-hero">
        <Container>
          <div className="app-hero-content">
            <div className="app-hero-title-row">
              <img src={appIcon} alt="Pool Tournament Manager" className="app-hero-icon" />
              <h1>Pool Tournament Manager</h1>
            </div>
            <p className="app-hero-subtitle">
              Run your pool tournaments from your phone. Brackets, killer pool,
              leagues, live rooms — everything you need, in your pocket.
            </p>
            <div className="app-hero-actions">
              <a
                href="https://play.google.com/store/apps/details?id=com.porkenpence.tournament_bracket"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={googlePlayBadge} alt="Get it on Google Play" className="google-play-badge" />
              </a>
              <a href="#features" className="btn-lg btn-outline-white">
                See Features
              </a>
            </div>
          </div>
        </Container>
      </section>

      {/* Screenshots placeholder */}
      <section className="app-screenshots" id="screenshots">
        <Container>
          <h2 className="section-title">See it in Action</h2>
          <div className="screenshots-grid">
            <div className="screenshot-frame">
              <img src={homeImg} alt="Tournament list with PRO badge and management options" loading="lazy" decoding="async" />
              <span className="screenshot-label">Manage Tournaments</span>
            </div>
            <div className="screenshot-frame screenshot-frame--hero">
              <img src={bracketImg} alt="Live bracket view with champion crowned" loading="lazy" decoding="async" />
              <span className="screenshot-label">Live Brackets</span>
            </div>
            <div className="screenshot-frame">
              <img src={roomImg} alt="Create a live tournament room" loading="lazy" decoding="async" />
              <span className="screenshot-label">Create Room</span>
            </div>
            <div className="screenshot-frame">
              <img src={liveRoomImg} alt="Live room with room code and scorer code" loading="lazy" decoding="async" />
              <span className="screenshot-label">Share Room</span>
            </div>
            <div className="screenshot-frame">
              <img src={scorerImg} alt="Enter scorer code to submit scores remotely" loading="lazy" decoding="async" />
              <span className="screenshot-label">Remote Scoring</span>
            </div>
          </div>
        </Container>
      </section>

      {/* Free features */}
      <section className="app-features" id="features">
        <Container>
          <h2 className="section-title">Free Features</h2>
          <p className="section-subtitle">Everything you need to run tournaments locally — completely free.</p>
          <div className="features-grid">
            {FREE_FEATURES.map((f) => (
              <div key={f.title} className="feature-card">
                <span className="feature-icon">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Pro features */}
      <section className="app-pro" id="pro">
        <Container>
          <div className="pro-badge" aria-label="PRO" />
          <h2 className="section-title">Go Live with Pro</h2>
          <p className="section-subtitle">
            Share your tournament with the world. Viewers follow along in
            real time on any device — no app needed.
          </p>
          <div className="features-grid features-grid--pro">
            {PRO_FEATURES.map((f) => (
              <div key={f.title} className="feature-card feature-card--pro">
                <span className="feature-icon">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="web-viewer-showcase">
            <h3>Watch from Any Browser</h3>
            <p>Viewers follow along at pool.porkenpence.com — no app or sign-up required.</p>
            <img src={webViewerImg} alt="Web browser showing live tournament bracket" loading="lazy" decoding="async" />
          </div>
        </Container>
      </section>

      {/* How it works */}
      <section className="app-how-it-works">
        <Container>
          <h2 className="section-title">How Live Rooms Work</h2>
          <div className="steps-grid">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Create a Tournament</h3>
              <p>Set up your bracket, add players, and choose the format.</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Go Live</h3>
              <p>Tap "Go Live" to create a room. Share the code or link with viewers.</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Watch Anywhere</h3>
              <p>Viewers open the link in any browser or join via the app. No sign-up needed.</p>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="app-cta">
        <Container>
          <h2>Ready to run your next tournament?</h2>
          <p>Download the app and get started in seconds.</p>
          <a
            href="https://play.google.com/store/apps/details?id=com.porkenpence.tournament_bracket"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={googlePlayBadge} alt="Get it on Google Play" className="google-play-badge" />
          </a>
        </Container>
      </section>
    </div>
  );
}
